import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { makeDomainQueryOptions } from '#api/domains.queries.ts';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useAddAdminOIDCProvider, useEditAdminOIDCProvider } from '#api/oidc-providers.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/error/ErrorMessage.tsx';
import { Input } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { OIDCProvider } from '#dn-types/oidc-provider.ts';
import { NEW_DOMAIN_FLAG, NO_DOMAIN_FLAG } from '../../SelectDomain.queries.ts';
import { SelectDomain } from '../../SelectDomain.tsx';
import { useCreatableDomain } from '../hooks/useCreatableDomain.ts';
import { IdentityProviderBaseForm } from '../IdentityProviderBaseForm.tsx';
import {
  addAdminIdentityProviderFormSchema,
  editAdminIdentityProviderFormSchema,
  type AddAdminIdentityProviderFormData,
  type EditAdminIdentityProviderFormData,
} from './AdminIdentityProviderForm.schema.ts';

type EditProps = {
  adminIdentityProviderId: string;
  defaultValues: OIDCProvider;
  defaultDomainId?: undefined;
  onCancel: () => void;
  setGlobalErrorMsg: (message: string | null) => void;
};

type AddProps = {
  adminIdentityProviderId: null;
  /** This will be specified when creating a provider from an unused domain */
  defaultDomainId?: string;
  onCancel: () => void;
  setGlobalErrorMsg: (message: string | null) => void;
};

const DEFAULT_VALUES = {
  clientID: '',
  clientSecret: '',
  issuerURL: '',
  domainID: '',
  domainName: '',
} satisfies AddAdminIdentityProviderFormData;

export function AdminIdentityProviderForm(props: EditProps | AddProps) {
  const { adminIdentityProviderId, setGlobalErrorMsg, defaultDomainId } = props;
  const queryClient = useQueryClient();
  const addOIDCProviderMutation = useAddAdminOIDCProvider();
  const editOIDCProviderMutation = useEditAdminOIDCProvider();
  const navigate = useNavigate();

  const defaultValues =
    adminIdentityProviderId ?
      ({
        clientID: props.defaultValues.clientID,
        clientSecret: '',
        issuerURL: props.defaultValues.issuerURL,
        domainID: props.defaultValues.domainID ?? NO_DOMAIN_FLAG,
        domainName: '',
      } satisfies EditAdminIdentityProviderFormData)
    : { ...DEFAULT_VALUES, domainID: defaultDomainId ?? '' };
  const isEditing = adminIdentityProviderId !== null;
  const schema = isEditing ? editAdminIdentityProviderFormSchema : addAdminIdentityProviderFormSchema;

  const formContext = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const {
    handleSubmit,
    setError,
    control,
    register,
    formState: { errors, isSubmitting, isSubmitted, isDirty, isValid },
  } = formContext;
  const lens = useLens({ control });
  const { createOrUseExistingDomain, hasCreatedDomain } = useCreatableDomain(formContext);

  /**
   * Submission does two things:
   *
   * 1) Creates a new domain if necessary
   * 2) Creates/edits the admin oidc provider, using either the new domain or a provided domainId
   *
   * Note, there's a possible intermediate state, where the domain creation succeeded,
   * but the provider submission failed.  In this case, we'll use the ID from the
   * previously-created new domain.
   */
  const onSubmit = handleSubmit(async (data) => {
    setGlobalErrorMsg(null);

    // The API rejects an enabled provider with an unverified domain.  Catch that here, before
    // creating a new domain, so a rejected submission doesn't leave a half-finished state.
    if (isEditing && props.defaultValues.enabled && data.domainID !== NO_DOMAIN_FLAG) {
      const isVerified =
        data.domainID === NEW_DOMAIN_FLAG ?
          false
        : (await queryClient.fetchQuery(makeDomainQueryOptions(data.domainID))).data.isVerified;
      if (!isVerified) {
        setError(
          'domainID',
          { message: 'Please disable the provider before assigning an unverified domain' },
          { shouldFocus: true }
        );
        return;
      }
    }

    const domainID = await createOrUseExistingDomain(data);

    // Only try to save the provider if we didn't blow up when creating the domain
    if (typeof domainID === 'undefined') {
      return;
    }

    let shouldVerifyDomain = false;
    if (domainID) {
      const { data: domain } = await queryClient.fetchQuery(makeDomainQueryOptions(domainID));
      if (!domain.isVerified) {
        shouldVerifyDomain = true;
      }
    }

    function handleSuccess(provider: OIDCProvider) {
      if (shouldVerifyDomain && provider.domainID) {
        void navigate(`/settings/sso/domains/${provider.domainID}/verify?tab=admin`);
        return;
      }
      void navigate('/settings/sso?tab=admin');
    }

    try {
      if (isEditing) {
        await editOIDCProviderMutation.mutateAsync(
          {
            id: adminIdentityProviderId,
            values: {
              clientID: data.clientID,
              // We need to send a `null` to signal to not change the client secret. Sending an empty string will error.
              clientSecret: data.clientSecret.length > 0 ? data.clientSecret : null,
              issuerURL: data.issuerURL,
              enabled: props.defaultValues.enabled,
              domainID,
            },
          },
          { onSuccess: handleSuccess }
        );
      } else {
        await addOIDCProviderMutation.mutateAsync(
          {
            clientID: data.clientID,
            clientSecret: data.clientSecret,
            issuerURL: data.issuerURL,
            // The provider can't be enabled until its domain has been verified
            enabled: !shouldVerifyDomain,
            domainID,
          },
          { onSuccess: handleSuccess }
        );
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        handleServerFormError(err, ({ path, message }) => {
          switch (path) {
            case 'clientID':
            case 'clientSecret':
            case 'issuerURL':
            case 'domainID':
            case 'domainName':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setGlobalErrorMsg(message);
              return true;
            default:
              setGlobalErrorMsg('We couldn’t save your changes, please try again');
              return false;
          }
        });
      } else {
        trackError(err);
        setGlobalErrorMsg('We couldn’t save your changes, please try again');
      }
    }
  });

  const domainIDValue = useWatch({ control, name: 'domainID' });

  return (
    <form action="POST" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide" className="mb-40">
        {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
        <IdentityProviderBaseForm
          isEditing={isEditing}
          lens={lens.reflect((fields) => ({
            clientID: fields.clientID,
            clientSecret: fields.clientSecret,
            issuerURL: fields.issuerURL,
          }))}
        />
        <SelectDomain
          name="domainID"
          label="Domain"
          control={control}
          disabled={hasCreatedDomain}
          rules={{ deps: 'domainName' }}
          errorMessage={errors.domainID?.message}
          helpTip="Admins with an email address at this domain can log in with SSO straight from the login page.  You will need to verify that you own this domain before it can be used."
        />
        {domainIDValue === NEW_DOMAIN_FLAG ?
          <Field label="New domain">
            <Input
              className="width-100"
              hasError={Boolean(errors.domainName)}
              {...register('domainName', { disabled: hasCreatedDomain })}
            />
            {errors.domainName?.message && <ErrorMessage className="mb-8">{errors.domainName.message}</ErrorMessage>}
          </Field>
        : null}
      </Spacer>
      <ConfirmChangesBanner
        isOpen={isDirty}
        title={isSubmitted && !isValid ? 'Unable to save.  Please update and try again.' : undefined}
        primaryButton={
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            Save
          </Button>
        }
        secondaryButton={
          <Button
            onClick={() => {
              props.onCancel();
            }}
            variant="link"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        }
      />
    </form>
  );
}
